import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { setTenantHeader, API } from '../lib/api';
import { ShoppingCart, Search, Plus, Minus, X, Filter, EyeOff, Share2, ChevronLeft, ChevronRight, Receipt, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { TenantManifest } from '../components/TenantManifest';

export const Catalog = () => {
  const { slug } = useParams();
  const { tenant: contextTenant } = useTenant();
  const [tenantReady, setTenantReady] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [myOrdersOpen, setMyOrdersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [galleryModal, setGalleryModal] = useState(null); // { slides: [{type:'video',url},{type:'image',url}], startIndex: 0 }
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const initTenant = async () => {
      // If we have context tenant already (subdomain-based), use it
      if (contextTenant?.id) {
        setTenantHeader(contextTenant.id);
        setTenantReady(true);
        return;
      }
      // If accessed via /loja/:slug, resolve the tenant
      if (slug) {
        try {
          const { data } = await axios.get(`${API}/tenant/resolve?subdomain=${slug}`);
          setTenantHeader(data.id);
          setTenantReady(true);
        } catch {
          toast.error('Loja não encontrada');
        }
        return;
      }
      // No tenant context - show catalog without filter (demo/legacy)
      setTenantReady(true);
    };
    initTenant();
  }, [slug, contextTenant]);

  useEffect(() => {
    if (!tenantReady) return;
    fetchSettings();
    fetchTenantInfo();
    fetchCategories();
    // iter77dg: NAO chama fetchProducts aqui — ele depende de settings.catalog_company_id
    // (senao a primeira renderizacao carrega produtos globais antes da regra STRICT
    // do catalogo aplicar). O useEffect abaixo cuida do fetch quando settings estiver pronto.
    fetchCart();
  }, [tenantReady]);

  useEffect(() => {
    // iter77dg: so busca produtos quando temos settings carregados. Se catalog_company_id
    // estiver definido, usa STRICT filter (so produtos vinculados). Se estiver vazio
    // (tenant sem filiais), busca sem company_id.
    if (!settings) return;
    fetchProducts();
    // iter77de: refetch quando a filial regente mudar (settings.catalog_company_id).
  }, [selectedCategory, searchTerm, sortBy, settings?.catalog_company_id, settings]);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data);
    } catch (error) {
      console.error('Fetch settings error:', error);
    }
  };

  const fetchTenantInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/tenant-info`);
      setTenantInfo(data);
    } catch {}
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data.data || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      if (sortBy) params.append('sort', sortBy);
      params.append('public', 'true');
      // iter77de: se o tenant definiu uma filial regente do catalogo, passa como company_id
      // para filtrar produtos visiveis pra essa filial + trazer estoque especifico dela.
      if (settings?.catalog_company_id) params.append('company_id', settings.catalog_company_id);
      
      const { data } = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(data.data || []);
    } catch (error) {
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch (error) {
      console.error('Fetch cart error:', error);
    }
  };

  const updateCartQuantity = async (productId, quantity) => {
    if (quantity < 1) {
      await removeFromCart(productId);
      return;
    }
    // Client-side stock guard — respect block_catalog_no_stock toggle. We look
    // up the product by id from the loaded list to know its stock.
    if (settings?.block_catalog_no_stock) {
      const prod = products.find(p => p.id === productId);
      const stock = prod?.stock != null ? Number(prod.stock) : null;
      if (stock !== null && quantity > stock) {
        toast.error(`${prod?.name || 'Produto'} tem apenas ${stock} em estoque`);
        return;
      }
    }
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity }, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      toast.error('Erro ao atualizar quantidade');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      console.error('Remove from cart error:', error);
    }
  };

  const getProductCartQuantity = (productId) => {
    const item = cart.items.find(i => i.product.id === productId);
    return item ? item.quantity : 0;
  };

  // Deep link: o link compartilhado abre o catalogo direto no produto (?produto=<id>)
  const productLink = (product) => `${window.location.origin}${window.location.pathname}?produto=${product.id}`;
  const shareProduct = (product) => {
    const text = `Confira: ${product.name} - R$ ${product.price.toFixed(2)}\n${productLink(product)}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // Ao abrir via deep link: garante o produto na lista, rola ate ele, destaca e abre a galeria
  const focusProductId = new URLSearchParams(window.location.search).get('produto');
  const [focusDone, setFocusDone] = useState(false);
  useEffect(() => {
    if (!focusProductId || focusDone || loading || !settings) return;
    let cancelled = false;
    (async () => {
      let prod = products.find(p => p.id === focusProductId);
      if (!prod) {
        try {
          const { data } = await axios.get(`${API}/products/${focusProductId}`);
          if (data?.id && !cancelled) { prod = data; setProducts(prev => [data, ...prev.filter(p => p.id !== data.id)]); }
        } catch { /* produto nao encontrado */ }
      }
      if (cancelled) return;
      setFocusDone(true);
      if (!prod) { toast.error('Produto não encontrado'); return; }
      setTimeout(() => {
        const el = document.getElementById(`produto-${focusProductId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.boxShadow = `0 0 0 4px #fff, 0 0 0 8px ${accentColor}`;
          setTimeout(() => { el.style.boxShadow = ''; }, 6000);
        }
        const slides = [];
        const yt = extractYouTubeId(prod.video_url);
        if (yt) slides.push({ type: 'video', youtubeId: yt });
        (prod.images || []).forEach(img => slides.push({ type: 'image', url: img }));
        if (slides.length > 0) setGalleryModal({ slides, startIndex: 0 });
      }, 400);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusProductId, focusDone, loading, settings, products]);

  const extractYouTubeId = (url) => {
    if (!url || url === 'None' || url === 'null') return null;
    const trimmed = url.trim();
    // Support iframe embed code: extract src URL
    const iframeMatch = trimmed.match(/src=["']([^"']+youtube[^"']+)["']/i);
    if (iframeMatch) {
      const embedUrl = iframeMatch[1];
      const embedId = embedUrl.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
      if (embedId) return embedId[1];
    }
    // Support: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/v/ID, youtube.com/shorts/ID
    const patterns = [
      /(?:youtube\.com\/(?:watch\?.*v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/,
      /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    ];
    for (const p of patterns) {
      const m = trimmed.match(p);
      if (m) return m[1];
    }
    // If it's just an 11-char ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
    return null;
  };

  const extractInstagramId = (url) => {
    if (!url) return null;
    const trimmed = url.trim();
    const patterns = [
      /instagram\.com\/(?:reel|p|reels)\/([A-Za-z0-9_-]+)/,
    ];
    for (const p of patterns) {
      const m = trimmed.match(p);
      if (m) return m[1];
    }
    return null;
  };

  const gridCols = settings ? (window.innerWidth < 768 ? settings.grid_mobile : settings.grid_desktop) : 2;
  const cartItemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
  const accentColor = settings?.accent_color || '#047857';
  const headerBg = settings?.header_bg_color || '#FFFFFF';
  const headerText = settings?.header_text_color || '#111111';
  const cardBg = settings?.card_bg_color || '#FFFFFF';
  const cardText = settings?.card_text_color || '#111111';
  const priceColor = settings?.price_color || '#111111';
  const buttonColor = settings?.button_color || accentColor;
  const buttonTextColor = settings?.button_text_color || '#FFFFFF';
  const pageBg = settings?.page_bg_color || settings?.secondary_color || '#FAFAFA';
  const categoryActiveColor = settings?.category_active_color || '#111111';
  const footerBg = settings?.footer_bg_color || '#FFFFFF';
  const mediaFormat = settings?.media_format || '1080x1080';

  const handleOpenCheckout = () => {
    if (cart.items.length === 0) {
      toast.error('Adicione produtos ao carrinho primeiro');
      return;
    }
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg, paddingBottom: '56px' }}>
      <TenantManifest target="catalog" />
      <header className="sticky top-0 z-50 transition-all duration-300 shadow-sm" style={{ backgroundColor: headerBg }}>
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center space-x-2.5 min-w-0">
            {settings?.logo_url && !scrolled && (
              <img
                src={settings.logo_url}
                alt=""
                className="h-8 transition-all duration-300 flex-shrink-0 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <h1 className={`truncate transition-all duration-300 ${scrolled ? 'text-sm' : 'text-base'}`} style={{ color: headerText, fontFamily: "'Inter', sans-serif", fontWeight: 300, letterSpacing: '0.02em' }}>
              {(settings?.store_name || 'Catalogo').split(' ')[0]}
            </h1>
          </div>
          <div className="flex items-center space-x-1">
            <button onClick={() => setSearchOpen(true)} className="p-2 rounded-full" style={{ color: headerText }} data-testid="header-search-btn">
              <Search size={20} />
            </button>
            <button onClick={() => setMyOrdersOpen(true)} className="p-2 rounded-full" style={{ color: headerText }} title="Meus Pedidos" data-testid="header-my-orders-btn">
              <Receipt size={20} />
            </button>
            <button onClick={() => setCartOpen(true)} className="p-2 rounded-full relative" style={{ color: headerText }} data-testid="header-cart-btn">
              <ShoppingCart size={20} />
              {cartItemCount > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{cartItemCount}</span>
              )}
            </button>
          </div>
        </div>
        {settings?.banner_url && !scrolled && (
          <div className="px-4 pb-2.5 transition-all duration-300">
            <img src={settings.banner_url} alt="Banner" className="w-full h-24 md:h-32 object-cover rounded-lg" />
          </div>
        )}
      </header>

      <div className="bg-white border-b border-zinc-200 py-4">
        <div className="flex justify-center overflow-x-auto px-4">
          <div className="flex space-x-4">
            <button onClick={() => setSelectedCategory(null)} className="flex flex-col items-center flex-shrink-0">
              <div className="w-16 h-16 rounded-full border-3 flex items-center justify-center mb-2" style={{ borderColor: !selectedCategory ? categoryActiveColor : '#E5E7EB', borderWidth: '3px' }}>
                <span className="text-2xl">🏠</span>
              </div>
              <span className="text-xs font-medium">Todos</span>
            </button>
            {/* iter77bt: apenas categorias-pai no menu circular; subs viram chips abaixo */}
            {categories.filter(c => !c.parent_id).map((cat) => {
              const subs = categories.filter(c => c.parent_id === cat.id);
              const selectedSubIsChild = subs.some(s => s.id === selectedCategory);
              const active = selectedCategory === cat.id || selectedSubIsChild;
              return (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className="flex flex-col items-center flex-shrink-0">
                  <div className="w-16 h-16 rounded-full border-3 flex items-center justify-center mb-2 overflow-hidden" style={{ borderColor: active ? categoryActiveColor : '#E5E7EB', borderWidth: '3px', backgroundColor: cat.image_url ? 'transparent' : 'white' }}>
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">📦</span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-center w-20 truncate">{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>
        {/* iter77bt: chips de subcategorias quando um pai (ou uma sub) esta selecionado */}
        {(() => {
          const currentParentId = (() => {
            if (!selectedCategory) return null;
            const c = categories.find(x => x.id === selectedCategory);
            if (!c) return null;
            return c.parent_id || c.id;
          })();
          const subs = currentParentId ? categories.filter(c => c.parent_id === currentParentId) : [];
          if (subs.length === 0) return null;
          return (
            <div className="flex justify-center flex-wrap gap-1.5 mt-3 px-4" data-testid="catalog-subcategory-chips">
              {subs.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setSelectedCategory(sub.id === selectedCategory ? currentParentId : sub.id)}
                  className={`text-[11px] px-3 py-1 rounded-full border transition-all ${sub.id === selectedCategory ? 'text-white' : 'text-zinc-600 border-zinc-200 hover:border-zinc-400 bg-white'}`}
                  style={sub.id === selectedCategory ? { backgroundColor: categoryActiveColor, borderColor: categoryActiveColor } : {}}
                  data-testid={`catalog-subcategory-${sub.slug}`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="bg-white border-b border-zinc-200 py-3 px-4">
        <div className="flex gap-2">
          <button onClick={() => setFilterOpen(true)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-zinc-300 rounded-full">
            <Filter size={16} />
            <span className="text-sm font-medium">Filtrar e ordenar</span>
          </button>
          <button onClick={() => setShowDetails(!showDetails)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 border border-zinc-300 rounded-full">
            <EyeOff size={16} />
            <span className="text-sm font-medium">Ocultar detalhes</span>
          </button>
        </div>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: accentColor }}></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-600">Nenhum produto encontrado</p>
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                quantity={getProductCartQuantity(product.id)}
                onUpdateQuantity={updateCartQuantity}
                onShare={shareProduct}
                onOpenGallery={setGalleryModal}
                showDetails={showDetails}
                accentColor={accentColor}
                index={index}
                extractYouTubeId={extractYouTubeId}
                extractInstagramId={extractInstagramId}
                categories={categories}
                settings={settings}
                colors={{ cardBg, cardText, priceColor, buttonColor, buttonTextColor }}
                mediaFormat={mediaFormat}
              />
            ))}
          </div>
        )}
      </div>

      {/* Barra inferior slim */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t" style={{ backgroundColor: footerBg, borderColor: `${headerText}10` }}>
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div style={{ color: headerText }}>
            <span className="text-sm font-bold">R$ {cart.total.toFixed(2)}</span>
            <span className="text-xs ml-1 opacity-60">({cartItemCount} {cartItemCount === 1 ? 'item' : 'itens'})</span>
          </div>
          <button onClick={handleOpenCheckout} className="px-5 py-2 rounded-full text-xs font-bold shadow-md transition-transform active:scale-95" style={{ backgroundColor: buttonColor, color: buttonTextColor }} data-testid="footer-checkout-btn">
            Finalizar Pedido
          </button>
        </div>
      </div>

      {/* Rodape informativo (nao fixo) */}
      <div className="px-4 pb-20 pt-8">
        <div className="border-t pt-6" style={{ borderColor: `${headerText}15` }}>
          <div className="text-center space-y-2">
            {settings?.store_name && <p className="text-xs font-semibold" style={{ color: `${headerText}BB` }}>{settings.store_name}</p>}
            {tenantInfo?.cnpj && <p className="text-[10px]" style={{ color: `${headerText}77` }}>CNPJ: {tenantInfo.cnpj}</p>}
            {tenantInfo?.address && <p className="text-[10px]" style={{ color: `${headerText}77` }}>{tenantInfo.address}</p>}
            <div className="flex items-center justify-center space-x-1.5 pt-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5" style={{ color: '#22c55e' }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span className="text-[10px] font-medium" style={{ color: '#22c55e' }}>Site Seguro - Certificado SSL</span>
            </div>
            <p className="text-[9px] pt-1" style={{ color: `${headerText}55` }}>Powered by 8IP Catalogo</p>
          </div>
        </div>
      </div>

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Sacola de produtos</SheetTitle>
          </SheetHeader>
          <CartDrawer cart={cart} onRemove={removeFromCart} onUpdateQuantity={updateCartQuantity} accentColor={accentColor} onCheckout={handleOpenCheckout} />
        </SheetContent>
      </Sheet>

      <Sheet open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Finalizar Pedido</SheetTitle>
          </SheetHeader>
          <CheckoutForm cart={cart} settings={settings} accentColor={accentColor} onClose={() => setCheckoutOpen(false)} />
        </SheetContent>
      </Sheet>

      <MyOrdersModal open={myOrdersOpen} onClose={() => setMyOrdersOpen(false)} accentColor={accentColor} headerBg={headerBg} headerText={headerText} />

      <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
        <SheetContent side="top">
          <SheetHeader>
            <SheetTitle>Pesquisar Produtos</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            <input type="text" placeholder="Digite o nome ou SKU..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2" autoFocus />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Filtrar e Ordenar</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Ordenar por:</label>
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setFilterOpen(false); }} className="w-full px-4 py-2 border border-zinc-300 rounded">
                <option value="newest">Mais novos</option>
                <option value="price_asc">Menor preço</option>
                <option value="price_desc">Maior preço</option>
                <option value="name_asc">Nome A-Z</option>
                <option value="name_desc">Nome Z-A</option>
              </select>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AnimatePresence>
        {galleryModal && (
          <GalleryModal
            slides={galleryModal.slides}
            startIndex={galleryModal.startIndex || 0}
            onClose={() => setGalleryModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ProductCard = ({ product, quantity, onUpdateQuantity, onShare, onOpenGallery, showDetails, accentColor, index, extractYouTubeId, extractInstagramId, categories, settings, colors, mediaFormat }) => {
  const { cardBg, cardText, priceColor, buttonColor, buttonTextColor } = colors || {};
  // iter77de: valor principal do catalogo (destaque no rodape do card).
  // Default "price"; usuario pode escolher "box_price" em Config > Catalogo > Regras.
  const mainPriceField = (settings?.catalog_main_price === 'box_price' && product.box_price) ? 'box_price' : 'price';
  const mainPrice = Number(product[mainPriceField] || product.price || 0);
  const aspectMap = { '1080x1080': '1/1', '1080x1320': '4/5', '1080x1920': '9/16' };
  const aspectRatio = aspectMap[mediaFormat] || '1/1';
  const videoUrl = (product.video_url && product.video_url !== 'None' && product.video_url !== 'null') ? product.video_url.trim() : null;
  const youtubeId = videoUrl ? extractYouTubeId(videoUrl) : null;
  const instagramId = videoUrl && !youtubeId ? extractInstagramId(videoUrl) : null;
  const hasImage = product.images && product.images.length > 0;
  const [thumbError, setThumbError] = useState(false);

  const ytThumb = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
  // Fallback strategy: 1st product image > company logo > null (will render text-only card)
  const companyLogo = settings?.company_logo_url || settings?.logo_url || null;
  const fallbackImg = hasImage ? product.images[0] : (companyLogo || null);
  const customThumb = product.video_thumbnail || null;
  const mediaUrl = customThumb ? customThumb : ((!thumbError && ytThumb) ? ytThumb : fallbackImg);
  const imageCount = (hasImage ? product.images.length : 0) + (youtubeId || instagramId ? 1 : 0);
  const showImageArea = !!mediaUrl;

  const catName = categories?.find(c => c.id === product.category_id)?.name;
  const s = (field, fallback = true) => settings?.[field] ?? fallback;

  const handleImageClick = () => {
    const slides = [];
    if (youtubeId) {
      slides.push({ type: 'video', youtubeId });
    } else if (instagramId) {
      slides.push({ type: 'instagram', instagramId });
    }
    if (hasImage) {
      product.images.forEach(img => slides.push({ type: 'image', url: img }));
    }
    if (slides.length > 0) {
      onOpenGallery({ slides, startIndex: 0 });
    }
  };

  return (
    <motion.div id={`produto-${product.id}`} data-testid={`product-card-${product.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: index * 0.02 }} className="rounded-lg overflow-hidden shadow-sm transition-shadow" style={{ backgroundColor: cardBg || '#FFFFFF' }}>
      {showImageArea ? (
      <div className="relative bg-zinc-100 cursor-pointer group overflow-hidden" style={{ aspectRatio }} onClick={handleImageClick} data-testid={`product-card-image-${product.id}`}>
        <img
          src={mediaUrl}
          alt={product.name}
          className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${hasImage || customThumb || ytThumb ? 'object-cover' : 'object-contain p-6 opacity-90'}`}
          onError={() => setThumbError(true)}
        />
        
        {imageCount > 1 && (
          <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
            1/{imageCount}
          </div>
        )}

        <button onClick={(e) => { e.stopPropagation(); onShare(product); }} className="absolute bottom-2 right-2 bg-white/90 rounded-full p-2 shadow-md" data-testid={`share-btn-${product.id}`}>
          <Share2 size={14} className="text-zinc-600" />
        </button>
      </div>
      ) : (
        <div className="relative cursor-pointer group flex items-center justify-center text-center px-3 py-6" style={{ aspectRatio, backgroundColor: cardBg ? `${cardBg}` : '#fafafa' }} onClick={handleImageClick} data-testid={`product-card-image-${product.id}`}>
          <h3 className="font-black text-sm md:text-base leading-tight line-clamp-3" style={{ color: cardText || '#111' }}>{product.name}</h3>
          <button onClick={(e) => { e.stopPropagation(); onShare(product); }} className="absolute bottom-2 right-2 bg-white/90 rounded-full p-2 shadow-md" data-testid={`share-btn-${product.id}`}>
            <Share2 size={14} className="text-zinc-600" />
          </button>
        </div>
      )}
      
      <div className="p-2.5">
        <h3 className="font-bold text-xs mb-1 line-clamp-2" style={{ color: cardText || '#111' }}>{product.name}</h3>
        {showDetails && (
          <div className="text-[11px] space-y-0.5 mb-1.5" style={{ color: cardText ? `${cardText}99` : '#71717a' }}>
            {s('show_brand') && product.brand && <p>Marca: {product.brand}</p>}
            {s('show_category') && catName && <p>Categoria: {catName}</p>}
            {s('show_grade') && product.grade && <p>Grade: {product.grade}</p>}
            {s('show_kit_qty') && product.box_quantity && <p>Kit com {product.box_quantity} peças</p>}
            {s('show_unit_price') && <p>Valor: R$ {product.price.toFixed(2)}</p>}
            {s('show_kit_price') && product.box_price && <p>Valor por item (unitário): R$ {product.box_price.toFixed(2)}</p>}
            {s('show_stock', false) && typeof product.stock === 'number' && <p>Estoque: {product.stock}</p>}
            {s('show_description', false) && product.description && <p>{product.description}</p>}
            {(s('show_reference') || s('show_weight')) && (
              <p>
                {s('show_reference') && (product.reference ? `Ref.: ${product.reference}` : `Ref.: ${product.sku}`)}
                {s('show_reference') && s('show_weight') && product.weight ? ' / ' : ''}
                {s('show_weight') && product.weight ? `Peso: ${product.weight}g` : ''}
              </p>
            )}
            {s('show_sku', false) && <p>SKU: {product.sku}</p>}
          </div>
        )}
        <div className="border-t pt-1.5" style={{ borderColor: cardText ? `${cardText}15` : '#e4e4e7' }}>
          {product.price_on_request ? (
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold" style={{ color: buttonColor || accentColor }}>Faça seu Orçamento</p>
              <button onClick={(e) => { e.stopPropagation(); onShare(product); }} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: buttonColor || accentColor, color: buttonTextColor || '#FFF' }} data-testid={`quote-btn-${product.id}`}>
                Consultar
              </button>
            </div>
          ) : quantity === 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold" style={{ color: priceColor || '#111' }}>R$ {mainPrice.toFixed(2)}</p>
              <button onClick={() => onUpdateQuantity(product.id, 1)} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: buttonColor || accentColor, color: buttonTextColor || '#FFF' }} data-testid={`add-btn-${product.id}`}>
                Adicionar
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold" style={{ color: priceColor || '#111' }}>R$ {(mainPrice * quantity).toFixed(2)}</p>
              <div className="flex items-center space-x-1.5">
                <button onClick={() => onUpdateQuantity(product.id, quantity - 1)} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: buttonColor || accentColor, color: buttonTextColor || '#FFF' }}>
                  <Minus size={14} />
                </button>
                <span className="font-bold text-sm w-5 text-center" style={{ color: cardText || '#111' }}>{quantity}</span>
                <button onClick={() => onUpdateQuantity(product.id, quantity + 1)} className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: buttonColor || accentColor, color: buttonTextColor || '#FFF' }}>
                  <Plus size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const GalleryModal = ({ slides, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goTo = useCallback((idx) => {
    if (idx >= 0 && idx < slides.length) setCurrent(idx);
  }, [slides.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goTo(current + 1);
      else goTo(current - 1);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goTo(current + 1);
      if (e.key === 'ArrowLeft') goTo(current - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [current, goTo, onClose]);

  const slide = slides[current];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50 flex flex-col"
      data-testid="gallery-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white z-10">
        <span className="text-sm font-medium">{current + 1}/{slides.length}</span>
        <button onClick={onClose} className="p-2" data-testid="gallery-close-btn">
          <X size={24} />
        </button>
      </div>

      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.2 }}
            className="w-full h-full flex items-center justify-center"
          >
            {slide.type === 'video' ? (
              <div className="w-full h-full flex items-center justify-center p-0">
                <iframe
                  src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&mute=0`}
                  title="Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full rounded-none"
                />
              </div>
            ) : slide.type === 'instagram' ? (
              <div className="w-full h-full flex items-center justify-center p-0">
                <iframe
                  src={`https://www.instagram.com/reel/${slide.instagramId}/embed`}
                  title="Instagram"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  allowTransparency="true"
                  scrolling="no"
                  className="w-full h-full rounded-none"
                />
              </div>
            ) : (
              <img
                src={slide.url}
                alt=""
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                data-testid={`gallery-slide-${current}`}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {current > 0 && (
          <button
            onClick={() => goTo(current - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white backdrop-blur-sm"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {current < slides.length - 1 && (
          <button
            onClick={() => goTo(current + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white backdrop-blur-sm"
            data-testid="gallery-next-btn"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {slides.length > 1 && (
        <div className="flex justify-center gap-2 py-4">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white w-6' : 'bg-white/40'}`}
              data-testid={`gallery-dot-${i}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

const CartDrawer = ({ cart, onRemove, onUpdateQuantity, accentColor, onCheckout }) => {
  if (cart.items.length === 0) {
    return (
      <div className="py-8 text-center">
        <ShoppingCart className="w-16 h-16 mx-auto text-zinc-300 mb-4" />
        <p className="text-zinc-600 font-bold">Ops, sua sacola está vazia</p>
        <p className="text-sm text-zinc-500 mt-2">Volte ao catálogo e adicione produtos</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {cart.items.map((item) => (
          <div key={item.product.id} className="flex gap-3 border-b border-zinc-200 pb-4">
            <img src={item.product.images?.[0] || 'https://images.pexels.com/photos/6791447/pexels-photo-6791447.jpeg'} alt={item.product.name} className="w-20 h-20 object-cover rounded" />
            <div className="flex-1">
              <h4 className="font-bold text-sm mb-1">{item.product.name}</h4>
              <p className="text-xs text-zinc-600">SKU {item.product.sku}</p>
              <p className="text-sm font-bold mt-1">R$ {((item.product.box_price || item.product.price) * item.quantity).toFixed(2)}</p>
              <div className="flex items-center space-x-2 mt-2">
                <button onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)} className="w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: accentColor }}>
                  <Minus size={12} />
                </button>
                <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                <button onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)} className="w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: accentColor }}>
                  <Plus size={12} />
                </button>
              </div>
            </div>
            <button onClick={() => onRemove(item.product.id)} className="text-red-500">
              <X size={20} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-zinc-200">
        <div className="flex justify-between mb-4">
          <span className="font-bold text-lg">Total:</span>
          <span className="font-bold text-lg">R$ {cart.total.toFixed(2)}</span>
        </div>
        <button data-testid="checkout-btn" onClick={onCheckout} className="w-full text-white py-3 rounded-lg font-bold" style={{ backgroundColor: accentColor }}>
          Finalizar Pedido
        </button>
      </div>
    </div>
  );
};


const CheckoutForm = ({ cart, settings, accentColor, onClose }) => {
  const [customerType, setCustomerType] = useState('pf');
  const [form, setForm] = useState({
    name: '', email: '', phone: '', cpf: '', birth: '',
    razao: '', cnpj: '', ie: '',
    cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '',
    observation: ''
  });
  const [sending, setSending] = useState(false);
  const purchaseMode = !!settings?.catalog_creates_purchase_request;
  const allowedCompanies = settings?.catalog_purchase_request_companies || [];
  const [targetCompanyId, setTargetCompanyId] = useState(allowedCompanies.length === 1 ? allowedCompanies[0].id : '');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 7) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  };

  const formatCPF = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };

  const formatCNPJ = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
    return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
  };

  const formatCEP = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    if (d.length <= 5) return d;
    return `${d.slice(0,5)}-${d.slice(5)}`;
  };

  const buildWhatsAppMessage = () => {
    const storeName = settings?.store_name || 'Catálogo';
    let msg = `*Pedido - ${storeName}*\n\n`;
    msg += `*Itens:*\n`;
    cart.items.forEach((item, i) => {
      const price = item.product.box_price || item.product.price;
      msg += `${i + 1}. ${item.product.name} (${item.product.sku})\n`;
      msg += `   Qtd: ${item.quantity} x R$ ${price.toFixed(2)} = R$ ${(price * item.quantity).toFixed(2)}\n`;
    });
    msg += `\n*Total: R$ ${cart.total.toFixed(2)}*\n\n`;

    msg += `*Dados do Cliente:*\n`;
    if (customerType === 'pf') {
      if (form.name) msg += `Nome: ${form.name}\n`;
      if (form.cpf) msg += `CPF: ${form.cpf}\n`;
      if (form.birth) msg += `Nascimento: ${form.birth}\n`;
    } else {
      if (form.razao) msg += `Razao Social: ${form.razao}\n`;
      if (form.cnpj) msg += `CNPJ: ${form.cnpj}\n`;
      if (form.ie) msg += `IE: ${form.ie}\n`;
    }
    if (form.email) msg += `Email: ${form.email}\n`;
    if (form.phone) msg += `Telefone: ${form.phone}\n`;

    if (settings?.checkout_address && (form.street || form.city)) {
      msg += `\n*Endereco:*\n`;
      if (form.cep) msg += `CEP: ${form.cep}\n`;
      if (form.street) msg += `${form.street}${form.number ? ', ' + form.number : ''}${form.complement ? ' - ' + form.complement : ''}\n`;
      if (form.neighborhood) msg += `Bairro: ${form.neighborhood}\n`;
      if (form.city || form.state) msg += `${form.city}${form.state ? '/' + form.state : ''}\n`;
    }

    if (form.observation) msg += `\n*Observacao:* ${form.observation}\n`;

    return msg;
  };

  const handleSubmit = async () => {
    if (settings?.checkout_phone && !form.phone) {
      toast.error('Informe seu telefone');
      return;
    }
    if (customerType === 'pf' && settings?.checkout_pf_name && !form.name) {
      toast.error('Informe seu nome');
      return;
    }
    if (purchaseMode && !targetCompanyId) {
      toast.error('Selecione a filial destino da Solicitacao de Compra');
      return;
    }

    setSending(true);
    const whatsappNumber = (settings?.whatsapp_number || '').replace(/\D/g, '');

    // In purchase-request mode, do NOT open WhatsApp — the request is sent
    // straight to the chosen filial and ONLY persisted server-side. In sales
    // mode (default), we keep the legacy WhatsApp message flow.
    if (!purchaseMode && !whatsappNumber) {
      toast.error('Numero do WhatsApp nao configurado pela loja');
      setSending(false);
      return;
    }
    const message = purchaseMode ? '' : buildWhatsAppMessage();

    // Save order to backend BEFORE opening WhatsApp so it appears in admin's
    // Pedidos de Venda + customer can lookup via "Meus Pedidos".
    let saveOk = true;
    try {
      const customerName = customerType === 'pf' ? form.name : form.razao;
      const customerDoc = customerType === 'pf' ? form.cpf : form.cnpj;
      const orderItems = cart.items.map(it => ({
        product_id: it.product.id,
        name: it.product.name,
        sku: it.product.sku || '',
        quantity: it.quantity,
        price: it.product.box_price || it.product.price,
      }));
      await axios.post(`${API}/orders`, {
        items: orderItems,
        total: cart.total,
        // iter77de: pedido vai pra filial regente do catalogo. Se estiver em purchaseMode
        // (compra entre filiais), usa a filial destino selecionada; caso contrario, usa a
        // filial regente configurada em Config > Catalogo (se houver).
        target_company_id: purchaseMode ? targetCompanyId : (settings?.catalog_company_id || null),
        customer_info: {
          name: customerName || '',
          razao_social: form.razao || '',
          cpf: form.cpf || '',
          cnpj: form.cnpj || '',
          phone: form.phone || '',
          email: form.email || '',
          document: customerDoc || '',
          address: form.street || '',
          city: form.city || '',
          state: form.state || '',
          observation: form.observation || '',
        }
      }, { withCredentials: true });
    } catch (err) {
      saveOk = false;
      if (purchaseMode) {
        // Purchase-mode failure is fatal — no WhatsApp fallback.
        toast.error(err.response?.data?.detail || 'Falha ao enviar solicitacao de compra');
        setSending(false);
        return;
      }
      console.error('Falha ao salvar pedido no historico:', err);
    }

    if (purchaseMode) {
      if (saveOk) toast.success('Solicitacao de Compra enviada!');
      setSending(false);
      onClose();
      return;
    }

    const whatsappUrl = `https://api.whatsapp.com/send?phone=55${whatsappNumber}&text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    toast.success('Pedido enviado!');
    setSending(false);
    onClose();
  };

  const showPF = settings?.checkout_pf_enabled !== false;
  const showPJ = settings?.checkout_pj_enabled;
  const inputClass = "w-full px-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50";

  return (
    <div className="mt-4 space-y-5 pb-4" data-testid="checkout-form">
      {/* Resumo do pedido */}
      <div className="bg-zinc-50 rounded-lg p-3">
        <h4 className="font-bold text-sm mb-2">Resumo do Pedido</h4>
        {cart.items.map((item) => (
          <div key={item.product.id} className="flex justify-between text-sm py-1">
            <span className="text-zinc-700">{item.quantity}x {item.product.name}</span>
            <span className="font-medium">R$ {((item.product.box_price || item.product.price) * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t border-zinc-200">
          <span>Total</span>
          <span>R$ {cart.total.toFixed(2)}</span>
        </div>
      </div>

      {/* Filial destino (modo Solicitacao de Compra) */}
      {purchaseMode && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="checkout-target-company-block">
          <h4 className="font-bold text-sm mb-1 text-violet-900">Filial destino *</h4>
          <p className="text-[11px] text-violet-700 mb-2">Para qual filial sera enviada esta solicitacao de compra?</p>
          {allowedCompanies.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Nenhuma filial foi habilitada pelo administrador. Contate a loja.</p>
          ) : (
            <select value={targetCompanyId} onChange={(e) => setTargetCompanyId(e.target.value)} className={`${inputClass} focus:ring-violet-500 border-violet-200`} data-testid="checkout-target-company-select" required>
              <option value="">Selecione uma filial...</option>
              {allowedCompanies.map(c => <option key={c.id} value={c.id}>{c.code} | {c.name}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Tipo de pessoa */}
      {(showPF || showPJ) && (
        <div>
          <label className="block text-sm font-semibold mb-2">Tipo de Cadastro</label>
          <div className="flex gap-2">
            {showPF && (
              <button
                data-testid="checkout-type-pf"
                onClick={() => setCustomerType('pf')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${customerType === 'pf' ? 'text-white border-transparent' : 'bg-white border-zinc-300 text-zinc-700'}`}
                style={customerType === 'pf' ? { backgroundColor: accentColor } : {}}
              >
                Pessoa Fisica
              </button>
            )}
            {showPJ && (
              <button
                data-testid="checkout-type-pj"
                onClick={() => setCustomerType('pj')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border transition-colors ${customerType === 'pj' ? 'text-white border-transparent' : 'bg-white border-zinc-300 text-zinc-700'}`}
                style={customerType === 'pj' ? { backgroundColor: accentColor } : {}}
              >
                Pessoa Juridica
              </button>
            )}
          </div>
        </div>
      )}

      {/* Campos PF */}
      {customerType === 'pf' && (
        <div className="space-y-3">
          {settings?.checkout_pf_name !== false && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Nome completo *</label>
              <input data-testid="checkout-pf-name" type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Seu nome" className={inputClass} style={{ '--tw-ring-color': accentColor }} />
            </div>
          )}
          {settings?.checkout_pf_cpf && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">CPF</label>
              <input data-testid="checkout-pf-cpf" type="text" value={form.cpf} onChange={(e) => handleChange('cpf', formatCPF(e.target.value))} placeholder="000.000.000-00" className={inputClass} />
            </div>
          )}
          {settings?.checkout_pf_birth && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Data de Nascimento</label>
              <input data-testid="checkout-pf-birth" type="date" value={form.birth} onChange={(e) => handleChange('birth', e.target.value)} className={inputClass} />
            </div>
          )}
        </div>
      )}

      {/* Campos PJ */}
      {customerType === 'pj' && showPJ && (
        <div className="space-y-3">
          {settings?.checkout_pj_razao && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Razao Social</label>
              <input data-testid="checkout-pj-razao" type="text" value={form.razao} onChange={(e) => handleChange('razao', e.target.value)} placeholder="Razao Social da empresa" className={inputClass} />
            </div>
          )}
          {settings?.checkout_pj_cnpj && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">CNPJ</label>
              <input data-testid="checkout-pj-cnpj" type="text" value={form.cnpj} onChange={(e) => handleChange('cnpj', formatCNPJ(e.target.value))} placeholder="00.000.000/0000-00" className={inputClass} />
            </div>
          )}
          {settings?.checkout_pj_ie && (
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Inscricao Estadual</label>
              <input data-testid="checkout-pj-ie" type="text" value={form.ie} onChange={(e) => handleChange('ie', e.target.value)} placeholder="Inscricao Estadual" className={inputClass} />
            </div>
          )}
        </div>
      )}

      {/* Email */}
      {settings?.checkout_email && (
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">E-mail</label>
          <input data-testid="checkout-email" type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="seu@email.com" className={inputClass} />
        </div>
      )}

      {/* Telefone */}
      {settings?.checkout_phone !== false && (
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">Telefone *</label>
          <input data-testid="checkout-phone" type="tel" value={form.phone} onChange={(e) => handleChange('phone', formatPhone(e.target.value))} placeholder="(00) 00000-0000" className={inputClass} />
        </div>
      )}

      {/* Endereco */}
      {settings?.checkout_address && (
        <div className="space-y-3">
          <label className="block text-sm font-semibold">Endereco</label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">CEP</label>
              <input data-testid="checkout-cep" type="text" value={form.cep} onChange={(e) => handleChange('cep', formatCEP(e.target.value))} placeholder="00000-000" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Estado</label>
              <input data-testid="checkout-state" type="text" value={form.state} onChange={(e) => handleChange('state', e.target.value.toUpperCase().slice(0,2))} placeholder="UF" className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-600 mb-1">Rua</label>
            <input data-testid="checkout-street" type="text" value={form.street} onChange={(e) => handleChange('street', e.target.value)} placeholder="Rua, Avenida..." className={inputClass} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Numero</label>
              <input data-testid="checkout-number" type="text" value={form.number} onChange={(e) => handleChange('number', e.target.value)} placeholder="N" className={inputClass} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-zinc-600 mb-1">Complemento</label>
              <input data-testid="checkout-complement" type="text" value={form.complement} onChange={(e) => handleChange('complement', e.target.value)} placeholder="Apto, Bloco..." className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Bairro</label>
              <input data-testid="checkout-neighborhood" type="text" value={form.neighborhood} onChange={(e) => handleChange('neighborhood', e.target.value)} placeholder="Bairro" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-600 mb-1">Cidade</label>
              <input data-testid="checkout-city" type="text" value={form.city} onChange={(e) => handleChange('city', e.target.value)} placeholder="Cidade" className={inputClass} />
            </div>
          </div>
        </div>
      )}

      {/* Observacao */}
      {settings?.checkout_observation && (
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">Observacao</label>
          <textarea data-testid="checkout-observation" value={form.observation} onChange={(e) => handleChange('observation', e.target.value)} placeholder="Alguma observacao sobre o pedido?" rows={3} className={`${inputClass} resize-none`} />
        </div>
      )}

      {/* Botao enviar */}
      <button
        data-testid={purchaseMode ? "checkout-submit-purchase" : "checkout-submit-whatsapp"}
        onClick={handleSubmit}
        disabled={sending}
        className="w-full text-white py-3 rounded-lg font-bold flex items-center justify-center space-x-2 disabled:opacity-50"
        style={{ backgroundColor: purchaseMode ? '#7C3AED' : '#25D366' }}
      >
        {purchaseMode ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        )}
        <span>{sending ? 'Enviando...' : (purchaseMode ? 'Enviar Solicitacao de Compra' : 'Enviar Pedido via WhatsApp')}</span>
      </button>
    </div>
  );
};


/* ========== MEUS PEDIDOS (Customer Self-Service Lookup) ========== */
const MyOrdersModal = ({ open, onClose, accentColor = '#25D366', headerBg = '#FFFFFF', headerText = '#111111' }) => {
  const [phone, setPhone] = useState(() => {
    try { return localStorage.getItem('catalog_last_phone') || ''; } catch { return ''; }
  });
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const fmtPhone = (raw) => {
    const d = (raw || '').replace(/\D/g, '').slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const search = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) { toast.error('Telefone invalido (minimo 8 digitos)'); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders/lookup?phone=${digits}`, { withCredentials: true });
      setOrders(data.data || []);
      try { localStorage.setItem('catalog_last_phone', phone); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar');
      setOrders([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (!open) { setOrders(null); } }, [open]);

  const statusInfo = (s) => {
    if (s === 'completed') return { label: 'Concluida', color: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 };
    if (s === 'cancelled') return { label: 'Cancelado', color: 'bg-rose-50 text-rose-700', Icon: XCircle };
    if (s === 'confirmed') return { label: 'Confirmado', color: 'bg-sky-50 text-sky-700', Icon: CheckCircle2 };
    return { label: 'Pendente', color: 'bg-amber-50 text-amber-700', Icon: Clock };
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: headerBg }}>
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
              <Receipt size={20} />
            </div>
            <div>
              <SheetTitle style={{ color: headerText }}>Meus Pedidos</SheetTitle>
              <p className="text-xs opacity-60" style={{ color: headerText }}>Informe seu telefone para consultar</p>
            </div>
          </div>
        </SheetHeader>
        <div className="mt-4 space-y-3 pb-4" data-testid="my-orders-form">
          <div className="flex gap-2">
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(fmtPhone(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
              placeholder="(11) 99999-9999"
              className="flex-1 px-4 py-3 border-2 border-gray-200 focus:border-gray-400 rounded-xl outline-none text-base font-medium"
              style={{ fontSize: '16px' }}
              data-testid="my-orders-phone"
            />
            <button onClick={search} disabled={loading} className="px-5 rounded-xl font-bold text-white disabled:opacity-50" style={{ backgroundColor: accentColor }} data-testid="my-orders-search-btn">
              {loading ? '...' : 'Buscar'}
            </button>
          </div>

          {orders === null && (
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <Receipt size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Digite seu telefone e clique em Buscar para ver seus pedidos.</p>
            </div>
          )}

          {orders !== null && orders.length === 0 && !loading && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="my-orders-empty">
              <p className="text-sm font-semibold text-amber-800">Nenhum pedido encontrado</p>
              <p className="text-xs text-amber-700 mt-1">Verifique se o telefone informado e o mesmo usado no checkout.</p>
            </div>
          )}

          {orders !== null && orders.length > 0 && (
            <div className="space-y-2" data-testid="my-orders-list">
              <p className="text-xs font-bold text-gray-500 uppercase">{orders.length} pedido{orders.length === 1 ? '' : 's'} encontrado{orders.length === 1 ? '' : 's'}</p>
              {orders.map(o => {
                const erp = o.erp;
                const effStatus = erp?.status || o.status || 'pending';
                const si = statusInfo(effStatus);
                return (
                  <div key={o.id} className="bg-white border border-gray-200 rounded-2xl p-3" data-testid="my-orders-row">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{erp?.number ? `#${erp.number}` : `#${o.id.slice(-6).toUpperCase()}`}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${si.color}`}>
                          <si.Icon size={11} />{si.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-400">{new Date(o.created_at).toLocaleDateString('pt-BR')} {new Date(o.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1.5">
                      {(o.items || []).slice(0, 3).map((it, idx) => (
                        <p key={idx} className="truncate">{it.quantity || 1}× {it.name || it.product_name}</p>
                      ))}
                      {(o.items || []).length > 3 && <p className="text-gray-400 italic">+{o.items.length - 3} item(ns)</p>}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500">{(o.items || []).length} item(ns)</span>
                      <span className="text-base font-black text-gray-900">R$ {(o.total || 0).toFixed(2).replace('.', ',')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
